import type { AgingSignal as AgingSignalValue } from '@clm/shared-types';

/**
 * How late a verification is.
 *
 * The arithmetic is **not** here and must not be. The thresholds live in
 * `clm.vw_verification_aging`'s `CASE` (high-stakes warns at 24h and escalates
 * at 48h, everything else warns at 168h) and the notification ladder reads the
 * same view — one mechanism, two uses (§3.2). A second copy in TypeScript is
 * exactly how the queue and the notifier come to disagree about whether a field
 * is late. So this component renders `age_days`, the view's own `aging_signal`,
 * the persisted `escalated_utc` and the server's `is_escalated`, and computes
 * nothing.
 *
 * `is_escalated` — not `aging_signal`, and not `escalated_utc` alone — decides
 * whether the row is on fire. `VerificationQueueItemSchema` makes it the single
 * truth for exactly the case the two raw columns get wrong: a row that
 * escalated and was then re-enqueued has its age reset, so `aging_signal` falls
 * back to `Normal` while `escalated_utc` stands, and such a row "must not
 * silently de-escalate". The server's `escalated_only` filter is that same
 * expression (`escalated_utc IS NOT NULL OR aging_signal = 'Escalate'`), so
 * reading anything else here makes the filter and the rows it returns
 * contradict each other on screen.
 *
 * Escalation carries a glyph and an accessible label as well as the `--bad`
 * colour. A reviewer scanning for what is on fire must not depend on being able
 * to see red.
 */
const SIGNAL_COPY: Readonly<
  Record<AgingSignalValue, { readonly label: string; readonly tone: string; readonly glyph: string }>
> = {
  Normal: { label: 'Within threshold', tone: 'out', glyph: '·' },
  Warn: { label: 'Approaching threshold', tone: 'bad', glyph: '!' },
  Escalate: { label: 'Escalated', tone: 'bad', glyph: '!!' },
  Settled: { label: 'Settled', tone: 'ok', glyph: '·' },
};

export function AgingSignal({
  age_days,
  aging_signal,
  escalated_utc,
  is_escalated,
}: {
  readonly age_days: number;
  readonly aging_signal: AgingSignalValue;
  readonly escalated_utc: string | null;
  readonly is_escalated: boolean;
}) {
  // Settled wins over escalation: the field has been answered, and telling a
  // reviewer that a closed row is on fire sends them to look at nothing.
  const showEscalated = is_escalated && aging_signal !== 'Settled';
  const copy = showEscalated ? SIGNAL_COPY.Escalate : SIGNAL_COPY[aging_signal];
  const days = `${String(age_days)} ${age_days === 1 ? 'day' : 'days'}`;

  // The escalation stamp goes into the accessible name, so "12 days ·
  // escalated" is what a screen reader hears rather than "12 days". A settled
  // row that once escalated keeps the stamp in its name while reading as
  // settled — the history is evidence, not a live alarm.
  const name = showEscalated
    ? `${days} old, escalated${escalated_utc === null ? '' : ` at ${escalated_utc}`}`
    : `${days} old, ${copy.label.toLowerCase()}${
        escalated_utc === null ? '' : `, escalated at ${escalated_utc}`
      }`;

  return (
    // The full detail rides in a visually hidden span rather than in an
    // `aria-label`: `aria-label` on a roleless generic element is ignored by
    // several screen readers, and this stamp is the whole point of the column
    // (CLM-51). `title` stays for the pointer tooltip.
    <span className="aging" title={name}>
      <span className="aging-days">{days}</span>
      {is_escalated || aging_signal !== 'Normal' ? (
        <span className={`tag ${copy.tone}`}>
          <span aria-hidden="true">{copy.glyph} </span>
          {copy.label}
        </span>
      ) : null}
      <span className="vh"> — {name}</span>
    </span>
  );
}
